import { useState } from "react";

import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { FilterQueryOptions } from "../../TreePicklistItemProvider";
import { FilterBuilder } from "../../../Common/FilterBuilder";
import { useTreePicklistItemFabric } from "../../TreePicklistModal/useTreePicklistItemFabric";
import { TreePicklistItem } from "../../TreePicklistItem";

import { CommonTreeNodeProps } from "./ITreeNodeProps";
import { RequestNodesCallback, useNodesResponse, UseRequestNodesEffect } from "./useNodesResponse";

interface ChildrenNodesResponse {
    children: TreePicklistItem[];
    loading: boolean;
}

export const useChildrenNodes = (props: CommonTreeNodeProps, summaryOpened: boolean): ChildrenNodesResponse => {
    const useRequestChildrenNodesEffect = createRequestChildrenNodesEffect({
        elementId: props.item.elementId,
        childrenCount: props.item.childrenCount,
        summaryOpened: summaryOpened,
        childColumnName: props.childColumnName,
    });

    const createTreePicklistItem = useTreePicklistItemFabric(props);

    const { nodeList, loading } = useNodesResponse({
        props: props,
        onLoadingError: props.onLoadingError,
        useRequestNodesEffect: useRequestChildrenNodesEffect,
    });

    const childrenResponse = nodeList.filter(item => item[props.childColumnName] === props.item.elementId);
    const ancestorsResponse = nodeList.filter(item => item[props.childColumnName] !== props.item.elementId);

    const children = childrenResponse.map(item => createTreePicklistItem(item, ancestorsResponse));

    return { children: children, loading: loading };
};

const createRequestChildrenNodesEffect =
    (params: Omit<RequestChildrenNodesParams, "requestNodes">): UseRequestNodesEffect =>
    requestNodes => {
        useRequestChildrenNodesEffect({ ...params, requestNodes: requestNodes });
    };

interface RequestChildrenNodesParams {
    elementId: string;
    childrenCount: number;
    summaryOpened: boolean;
    childColumnName: string;
    requestNodes: RequestNodesCallback;
}

const useRequestChildrenNodesEffect = ({
    elementId,
    childrenCount,
    summaryOpened,
    childColumnName,
    requestNodes,
}: RequestChildrenNodesParams) => {
    const [isNodesAlreadyFetched, setIsNodesAlreadyFetched] = useState(false);

    useAsyncEffect(
        async signal => {
            const hasChildrenNodes = childrenCount > 0;
            const shouldRequestNodes = hasChildrenNodes && summaryOpened && !isNodesAlreadyFetched;

            if (!shouldRequestNodes) {
                return;
            }

            const filterBuilder = new FilterBuilder().column(childColumnName).eql(elementId);

            const filterQueryOptions: FilterQueryOptions = {
                nodeFilter: filterBuilder,
            };

            await requestNodes(filterQueryOptions, signal.aborted);
            if (!signal.aborted) {
                setIsNodesAlreadyFetched(true);
            }
        },
        [summaryOpened]
    );
};
